/**
 * useHistoryView ✦ — iter197
 * ─────────────────────────────────────────────────────────────
 * Sync a local "view" state machine with the browser history
 * stack so deep modal/capture surfaces (Booth OS subviews,
 * fullscreen captures, in-page wizards) behave like real routes
 * for browser back/forward.
 *
 * Before this hook, Booth OS held its current screen ('launcher'
 * | 'memory_snap' | 'photo_strip' | …) in plain React state.
 * That meant pressing the device back button would EXIT Booth OS
 * entirely — kicking the guest out to /event/:slug — instead of
 * returning them to the launcher. That violated the universal
 * back contract:
 *
 *   "Back should feel like moving through memory, not navigating
 *    software."
 *
 * Contract:
 *   • When `view` changes AWAY from `rootView`, pushState an
 *     entry tagged with the new view name.
 *   • When `view` changes BACK to `rootView`, history.back() so
 *     we don't pile entries.
 *   • On popstate, if the popped entry was one of our tagged
 *     entries, restore that view; otherwise reset to rootView.
 *   • The hook is idempotent — setting `view` to its current
 *     value is a no-op.
 *
 * Usage:
 *
 *   const [view, setView] = useState('launcher');
 *   useHistoryView({
 *     view,
 *     setView,
 *     rootView: 'launcher',
 *     tag: `booth_os_${event_id}`,
 *     transientViews: ['attract'],
 *   });
 *
 * Pass `tag` so a tab with multiple in-app view machines doesn't
 * collide (Booth OS vs. a future kiosk wizard, etc.).
 *
 * iter213 ✦ — `transientViews` carries any view names that are
 * transient overlays rather than navigable screens (idle attract,
 * brief toasts, etc.). These never push or pop history entries —
 * a tap to dismiss must just flip state, never trigger popstate
 * cascades that can ping-pong the user back into the overlay or
 * land them in a stale tagged view from earlier in the session.
 */
import { useEffect, useRef } from 'react';

const KEY = '__eventdrop_view__';

export function useHistoryView({
  view, setView, rootView, tag = 'view', transientViews = [],
}) {
  const isRoot = view === rootView;
  const prevViewRef = useRef(view);
  const installedRef = useRef(false);
  const transientSet = new Set(transientViews);

  // Push a tagged history entry whenever we leave the rootView,
  // and call history.back() when we return so the stack stays
  // shallow (max one extra entry).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prev = prevViewRef.current;
    prevViewRef.current = view;

    if (prev === view) return;

    // iter213 ✦ — transient overlays never touch history. A view
    // transition INTO a transient overlay (e.g. idle attract) is a
    // pure overlay flip; a transition OUT of it back to root is
    // also overlay-only. This keeps the popstate cascade clean.
    if (transientSet.has(prev) || transientSet.has(view)) return;

    if (prev === rootView && view !== rootView) {
      // Entering a subview — push a tagged history entry.
      const next = { ...(window.history.state || {}), [KEY]: { tag, view } };
      try { window.history.pushState(next, '', window.location.href); }
      catch { /* opaque-history environment — no-op */ }
      installedRef.current = true;
      return;
    }

    if (prev !== rootView && view === rootView && installedRef.current) {
      // Returning to root via programmatic setView — pop our entry
      // so browser back from here exits the surface cleanly.
      try { window.history.back(); }
      catch { /* no-op */ }
      installedRef.current = false;
    }
  }, [view, rootView, tag]);

  // Listen for browser back/forward and translate to setView.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = (e) => {
      const state = e?.state || {};
      const tagged = state[KEY];
      // If the popped entry carries a view tag for THIS surface,
      // restore that view; otherwise the user has popped past our
      // entries → reset to root.
      if (tagged && tagged.tag === tag && tagged.view) {
        setView(tagged.view);
        installedRef.current = tagged.view !== rootView;
      } else {
        // Only reset if we currently aren't on root — avoids
        // stomping unrelated popstate events from outside the
        // surface.
        if (prevViewRef.current !== rootView) {
          setView(rootView);
          installedRef.current = false;
        }
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [setView, rootView, tag]);

  // Clean up on unmount — if we installed a history entry, leave
  // it (the navigation that unmounted us already handled the
  // stack). No-op required here; documenting intent.
}

export default useHistoryView;
